import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';
import { 
  Bell, 
  Shield, 
  Globe, 
  Database, 
  CreditCard, 
  Mail, 
  X, 
  Image as ImageIcon, 
  Upload, 
  Trash2, 
  Plus, 
  ArrowLeft, 
  ArrowRight, 
  RotateCcw,
  CheckCircle2,
  ExternalLink
} from 'lucide-react';
import img1 from '../../assets/1.png';
import img2 from '../../assets/2.png';
import img3 from '../../assets/3.png';

const SettingsScreen = () => {
  const { getConfig } = useAuth();
  const [searchParams] = useSearchParams();
  const [, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [showStoreInfoModal, setShowStoreInfoModal] = useState(false);
  const [showHeroModal, setShowHeroModal] = useState(searchParams.get('tab') === 'hero');
  const [heroImages, setHeroImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  const [storeInfoForm, setStoreInfoForm] = useState({
    storeName: '',
    storeEmail: '',
    storePhone: '',
    storeAddress: '',
    taxId: '',
    currency: 'INR'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (searchParams.get('tab') === 'hero') {
      setShowHeroModal(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/settings`, getConfig());
        setSettings(data);
        if (data) {
          setStoreInfoForm({
            storeName: data.storeName || '',
            storeEmail: data.storeEmail || '',
            storePhone: data.storePhone || '',
            storeAddress: data.storeAddress || '',
            taxId: data.taxId || '',
            currency: data.currency || 'INR'
          });
          setHeroImages(data.heroImages && data.heroImages.length > 0 ? data.heroImages : [img1, img2, img3]);
        }
        setLoading(false);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };
    fetchSettings();
  }, [getConfig]);

  const handleUpdateStoreInfo = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      const { data } = await axios.put(`${API_URL}/settings`, storeInfoForm, getConfig());
      setSettings(data);
      setShowStoreInfoModal(false);
      alert('Store settings updated successfully');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update settings');
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveHeroImages = async (imagesList) => {
    try {
      const { data } = await axios.put(`${API_URL}/settings`, { heroImages: imagesList }, getConfig());
      setSettings(data);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error(error);
      alert('Failed to save hero images. Please try again.');
    }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    setUploadingImage(true);

    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getConfig().headers
        },
      };
      const { data } = await axios.post(`${API_URL}/upload`, formData, config);
      const newImages = [...heroImages, data.image];
      setHeroImages(newImages);
      await saveHeroImages(newImages);
    } catch (error) {
      console.error(error);
      alert('Image upload failed. Please try again or use a direct URL.');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleReplaceSlot = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    setUploadingImage(true);

    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getConfig().headers
        },
      };
      const { data } = await axios.post(`${API_URL}/upload`, formData, config);
      const newImages = [...heroImages];
      newImages[index] = data.image;
      setHeroImages(newImages);
      await saveHeroImages(newImages);
    } catch (error) {
      console.error(error);
      alert('Image upload failed. Please try again.');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleAddUrlImage = async (e) => {
    e.preventDefault();
    if (!newImageUrl.trim()) return;

    const newImages = [...heroImages, newImageUrl.trim()];
    setHeroImages(newImages);
    setNewImageUrl('');
    await saveHeroImages(newImages);
  };

  const handleRemoveImage = async (index) => {
    const newImages = heroImages.filter((_, i) => i !== index);
    setHeroImages(newImages);
    await saveHeroImages(newImages);
  };

  const handleMoveImage = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= heroImages.length) return;

    const newImages = [...heroImages];
    const temp = newImages[index];
    newImages[index] = newImages[targetIndex];
    newImages[targetIndex] = temp;

    setHeroImages(newImages);
    await saveHeroImages(newImages);
  };

  const handleResetDefaults = async () => {
    if (window.confirm('Reset hero section back to original default banners?')) {
      const defaults = [img1, img2, img3];
      setHeroImages(defaults);
      await saveHeroImages(defaults);
    }
  };

  const handleItemClick = (itemName) => {
    if (itemName === 'Store Information') {
      setShowStoreInfoModal(true);
    } else if (itemName === 'Hero Section Photos') {
      setShowHeroModal(true);
    } else {
      alert('This setting is not implemented yet.');
    }
  };

  const getSlotName = (idx) => {
    if (idx === 0) return 'Slot 1 (Left)';
    if (idx === 1) return 'Slot 2 (Center)';
    if (idx === 2) return 'Slot 3 (Right)';
    return `Banner #${idx + 1}`;
  };

  const settingsGroups = [
    {
      title: 'Homepage & Branding',
      items: [
        { name: 'Hero Section Photos', icon: ImageIcon, desc: 'Add, replace, reorder, or remove home page banner photos.' },
        { name: 'Store Information', icon: Globe, desc: 'Store Name, email, phone, currency, and invoice details.' },
        { name: 'Notifications', icon: Bell, desc: 'Configure order and customer alert settings.' },
      ]
    },
    {
      title: 'Security & Access',
      items: [
        { name: 'Staff Accounts', icon: Shield, desc: 'Manage admin access and permissions.' },
        { name: 'API Configuration', icon: Database, desc: 'Manage Cloudinary and Cashfree keys.' },
      ]
    },
    {
      title: 'Payments & Communication',
      items: [
        { name: 'Payment Methods', icon: CreditCard, desc: 'Configure Cashfree, UPI, or COD.' },
        { name: 'Email Templates', icon: Mail, desc: 'Customize customer invoice emails.' },
      ]
    }
  ];

  return (
    <div className="p-8 space-y-12 max-w-5xl mx-auto">
      <div className="flex justify-between items-end">
        <div className="space-y-1">
          <h1 className="text-3xl font-black italic text-secondary-900 uppercase">Admin Settings</h1>
          <p className="text-secondary-500 font-medium">Configure store parameters, homepage banners, and billing.</p>
        </div>
        <button
          onClick={() => setShowHeroModal(true)}
          className="bg-primary-600 hover:bg-primary-700 text-white px-5 py-3 rounded-2xl font-bold flex items-center space-x-2 shadow-lg shadow-primary-200 transition-all text-sm"
        >
          <ImageIcon className="h-4 w-4" />
          <span>Edit Hero Photos</span>
        </button>
      </div>

      <div className="grid gap-12">
        {settingsGroups.map((group) => (
          <div key={group.title} className="space-y-6">
            <h2 className="text-xs font-black text-secondary-400 uppercase tracking-[0.2em] ml-2">{group.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {group.items.map((item) => (
                <button 
                  key={item.name}
                  onClick={() => handleItemClick(item.name)}
                  className="bg-white p-6 rounded-[32px] border border-secondary-100 shadow-sm hover:shadow-md hover:border-primary-100 transition-all text-left flex items-start space-x-5 group"
                >
                  <div className="p-4 bg-secondary-50 rounded-2xl group-hover:bg-primary-50 transition-colors">
                    <item.icon className="h-6 w-6 text-secondary-400 group-hover:text-primary-600 transition-colors" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-bold text-secondary-900">{item.name}</h3>
                    <p className="text-sm text-secondary-500 font-medium leading-relaxed">{item.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Hero Images Management Modal */}
      {showHeroModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-secondary-900/70 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-[36px] w-full max-w-3xl my-8 overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
            <div className="p-6 md:p-8 space-y-6">
              <div className="flex justify-between items-center pb-4 border-b border-secondary-100">
                <div>
                  <div className="flex items-center space-x-2">
                    <h2 className="text-2xl font-black italic text-secondary-900 uppercase tracking-tighter">Edit Hero Section Photos</h2>
                    {saveSuccess && (
                      <span className="inline-flex items-center space-x-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full animate-in fade-in">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        <span>Saved to Live Site!</span>
                      </span>
                    )}
                  </div>
                  <p className="text-secondary-400 text-xs font-medium mt-1">
                    These photos are shown on the homepage hero slider and 3-column banner grid.
                  </p>
                </div>
                <button 
                  onClick={() => setShowHeroModal(false)} 
                  className="p-2.5 hover:bg-secondary-100 rounded-2xl transition-all"
                >
                  <X className="h-5 w-5 text-secondary-400" />
                </button>
              </div>

              {/* Current Hero Photos Grid */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-black uppercase tracking-widest text-secondary-500">
                    Active Banners ({heroImages.length})
                  </span>
                  <button
                    onClick={handleResetDefaults}
                    className="text-xs font-bold text-secondary-500 hover:text-secondary-800 flex items-center space-x-1 transition-colors"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    <span>Restore Defaults</span>
                  </button>
                </div>

                {heroImages.length === 0 ? (
                  <div className="p-8 text-center bg-secondary-50 rounded-2xl border border-dashed border-secondary-200 text-secondary-400 text-sm font-medium">
                    No custom photos added. Default soap banners will be displayed on the homepage.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                    {heroImages.map((img, idx) => (
                      <div key={idx} className="flex flex-col bg-secondary-50 p-3 rounded-3xl border border-secondary-200 shadow-sm space-y-3">
                        <div className="flex justify-between items-center px-1">
                          <span className="text-[11px] font-black uppercase tracking-wider text-secondary-800">
                            {getSlotName(idx)}
                          </span>
                          <span className="text-[10px] font-bold text-secondary-400">Position {idx + 1}</span>
                        </div>

                        <div className="relative group rounded-2xl overflow-hidden aspect-[16/9] border border-secondary-200 shadow-sm bg-white">
                          <img 
                            src={img} 
                            alt={`Hero ${idx + 1}`} 
                            className="w-full h-full object-cover" 
                          />

                          {/* Hover Overlay Controls */}
                          <div className="absolute inset-0 bg-secondary-900/60 opacity-0 group-hover:opacity-100 transition-all flex flex-col justify-between p-3">
                            <div className="flex justify-end">
                              <button 
                                onClick={() => handleRemoveImage(idx)}
                                className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-lg transition-transform hover:scale-110"
                                title="Delete Photo"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>

                            <div className="flex justify-between items-center">
                              <button
                                onClick={() => handleMoveImage(idx, -1)}
                                disabled={idx === 0}
                                className="p-1.5 bg-white/90 hover:bg-white text-secondary-800 rounded-xl disabled:opacity-30 disabled:pointer-events-none transition-all"
                                title="Move Left"
                              >
                                <ArrowLeft className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleMoveImage(idx, 1)}
                                disabled={idx === heroImages.length - 1}
                                className="p-1.5 bg-white/90 hover:bg-white text-secondary-800 rounded-xl disabled:opacity-30 disabled:pointer-events-none transition-all"
                                title="Move Right"
                              >
                                <ArrowRight className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Direct Replace Slot Upload Button */}
                        <div className="pt-1">
                          <input 
                            type="file" 
                            id={`replace-slot-${idx}`}
                            accept="image/*"
                            onChange={(e) => handleReplaceSlot(idx, e)}
                            className="hidden"
                            disabled={uploadingImage}
                          />
                          <label 
                            htmlFor={`replace-slot-${idx}`}
                            className="w-full flex items-center justify-center space-x-1.5 py-2.5 px-3 bg-white hover:bg-primary-50 hover:text-primary-700 hover:border-primary-200 border border-secondary-200 text-secondary-700 rounded-xl text-xs font-bold transition-all cursor-pointer shadow-sm text-center"
                          >
                            <Upload className="h-3.5 w-3.5" />
                            <span>Replace {getSlotName(idx)}</span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add New Photo Section */}
              <div className="pt-4 border-t border-secondary-100 space-y-4">
                <h3 className="text-xs font-black uppercase tracking-widest text-secondary-900">Add New Photo</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Option 1: Upload File */}
                  <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-primary-300 hover:border-primary-500 rounded-2xl bg-primary-50/50 hover:bg-primary-50 transition-all cursor-pointer text-center group">
                    {uploadingImage ? (
                      <div className="flex flex-col items-center space-y-2">
                        <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                        <span className="text-xs font-bold text-primary-700">Uploading to Cloud...</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-7 w-7 text-primary-600 mb-2 group-hover:scale-110 transition-transform" />
                        <span className="text-xs font-black uppercase tracking-wider text-primary-900">Upload Image File</span>
                        <span className="text-[11px] text-secondary-500 mt-0.5">JPG, PNG, WebP recommended (16:9)</span>
                        <input 
                          type="file" 
                          accept="image/*"
                          onChange={handleUploadImage}
                          className="hidden"
                          disabled={uploadingImage}
                        />
                      </>
                    )}
                  </label>

                  {/* Option 2: Add by URL */}
                  <form onSubmit={handleAddUrlImage} className="flex flex-col justify-between p-4 bg-secondary-50 border border-secondary-200 rounded-2xl space-y-2">
                    <div className="space-y-1">
                      <span className="text-xs font-black uppercase tracking-wider text-secondary-800">Or Paste Image URL</span>
                      <input 
                        type="url"
                        value={newImageUrl}
                        onChange={(e) => setNewImageUrl(e.target.value)}
                        placeholder="https://example.com/photo.jpg"
                        className="w-full bg-white border border-secondary-200 rounded-xl px-3 py-2 text-xs font-medium text-secondary-900 focus:ring-2 focus:ring-primary-500 outline-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={!newImageUrl.trim()}
                      className="w-full bg-secondary-900 hover:bg-secondary-800 disabled:opacity-40 text-white py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center space-x-1.5"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      <span>Add by URL</span>
                    </button>
                  </form>
                </div>
              </div>

              {/* View Homepage Link */}
              <div className="pt-2 flex justify-between items-center text-xs">
                <a 
                  href="/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-primary-600 hover:underline font-bold inline-flex items-center space-x-1"
                >
                  <span>Preview Live Homepage</span>
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
                <button
                  onClick={() => setShowHeroModal(false)}
                  className="bg-secondary-900 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-secondary-800 transition-colors"
                >
                  Done
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Store Info Modal */}
      {showStoreInfoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-secondary-900/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-[40px] w-full max-w-2xl my-8 overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
            <div className="p-8 md:p-12 space-y-8">
              <div className="flex justify-between items-center pb-6 border-b border-secondary-100">
                <div>
                  <h2 className="text-2xl font-black italic text-secondary-900 uppercase tracking-tighter">Store Information</h2>
                  <p className="text-secondary-400 text-xs font-medium mt-1">Update your billing details used for invoices.</p>
                </div>
                <button onClick={() => setShowStoreInfoModal(false)} className="p-3 hover:bg-secondary-100 rounded-2xl transition-all">
                  <X className="h-6 w-6 text-secondary-400" />
                </button>
              </div>

              {loading ? (
                 <div className="text-center font-bold text-secondary-400">Loading...</div>
              ) : (
                <form onSubmit={handleUpdateStoreInfo} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-black text-secondary-900 uppercase tracking-widest">Store Name</label>
                      <input 
                        type="text" 
                        value={storeInfoForm.storeName}
                        onChange={(e) => setStoreInfoForm({...storeInfoForm, storeName: e.target.value})}
                        className="w-full bg-secondary-50 border border-secondary-200 rounded-2xl px-5 py-4 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-black text-secondary-900 uppercase tracking-widest">Tax ID (GST/VAT)</label>
                      <input 
                        type="text" 
                        value={storeInfoForm.taxId}
                        onChange={(e) => setStoreInfoForm({...storeInfoForm, taxId: e.target.value})}
                        className="w-full bg-secondary-50 border border-secondary-200 rounded-2xl px-5 py-4 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-black text-secondary-900 uppercase tracking-widest">Email</label>
                      <input 
                        type="email" 
                        value={storeInfoForm.storeEmail}
                        onChange={(e) => setStoreInfoForm({...storeInfoForm, storeEmail: e.target.value})}
                        className="w-full bg-secondary-50 border border-secondary-200 rounded-2xl px-5 py-4 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-black text-secondary-900 uppercase tracking-widest">Phone</label>
                      <input 
                        type="text" 
                        value={storeInfoForm.storePhone}
                        onChange={(e) => setStoreInfoForm({...storeInfoForm, storePhone: e.target.value})}
                        className="w-full bg-secondary-50 border border-secondary-200 rounded-2xl px-5 py-4 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                        required
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs font-black text-secondary-900 uppercase tracking-widest">Store Address</label>
                      <textarea 
                        value={storeInfoForm.storeAddress}
                        onChange={(e) => setStoreInfoForm({...storeInfoForm, storeAddress: e.target.value})}
                        className="w-full bg-secondary-50 border border-secondary-200 rounded-2xl px-5 py-4 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900 min-h-[100px]"
                        required
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs font-black text-secondary-900 uppercase tracking-widest">Currency</label>
                      <input 
                        type="text" 
                        value={storeInfoForm.currency}
                        onChange={(e) => setStoreInfoForm({...storeInfoForm, currency: e.target.value})}
                        className="w-full bg-secondary-50 border border-secondary-200 rounded-2xl px-5 py-4 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                        required
                      />
                    </div>
                  </div>
                  
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-primary-600 hover:bg-primary-700 text-white py-4 rounded-2xl font-black italic uppercase transition-all disabled:opacity-50 text-center shadow-lg shadow-primary-200"
                  >
                    {isSubmitting ? 'Saving...' : 'Save Store Information'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsScreen;
